import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Code, Plus, Save, Table2, Trash2 } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Input } from 'src/shared/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { Card } from 'src/shared/components/ui/card'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'src/shared/components/ui/toggle-group'

import { RawTableEditor } from './RawTableEditor'
import { formatDate } from '../utils'
import { DecorationUnitCost_ } from 'src/api/db/apiDecorationInfo'

export const DecorationUnitCostTable = ({
  data,
  vendor,
  printCode,
  isLoading,
  onSave,
  onAdd,
  onDelete,
}: {
  data: DecorationUnitCost_[]
  vendor: string
  printCode: string
  isLoading: boolean
  onSave: (
    updates: Array<{
      id: number
      colors?: number | null
      format?: string | null
      price?: number | null
      quantity?: number | null
      size?: string | null
      size_range?: string | null
      number_of_stitches?: string | null
    }>
  ) => Promise<void>
  onAdd: (insert: {
    vendor: string
    print_code: string
    colors?: number | null
    format?: string | null
    price?: number | null
    quantity?: number | null
    size?: string | null
    size_range?: string | null
    number_of_stitches?: string | null
  }) => Promise<void>
  onDelete: (id: number) => Promise<void>
}) => {
  const nextId = useRef(-1)
  const [entryMode, setEntryMode] = useState<'table' | 'raw'>('table')
  const [rows, setRows] = useState<DecorationUnitCost_[]>([])
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setRows(
      data.map((r) => ({
        id: r.id,
        colors: r.colors,
        format: r.format,
        price: r.price,
        quantity: r.quantity,
        size: r.size,
        size_range: r.size_range,
        number_of_stitches: r.number_of_stitches,
        created_at: r.created_at,
        print_code: r.print_code,
        vendor: r.vendor,
      }))
    )
  }, [data])

  const update = useCallback(
    (i: number, field: string, value: string) =>
      setRows((prev) =>
        prev.map((r, j) => (j === i ? { ...r, [field]: value } : r))
      ),
    []
  )

  const add = useCallback(
    () =>
      setRows((prev) => [
        ...prev,
        {
          id: nextId.current--,
          colors: null, // TODO: fix this
          format: null,
          price: null,
          quantity: null,
          size: null,
          size_range: null,
          number_of_stitches: null,
          created_at: new Date().toISOString(),
          print_code: printCode,
          vendor,
        },
      ]),
    [printCode, vendor]
  )

  const remove = useCallback(
    async (i: number) => {
      const r = rows[i]
      if (r?.id > 0 && onDelete) await onDelete(r.id)
      setRows((prev) => prev.filter((_, j) => j !== i))
    },
    [rows, onDelete]
  )

  const save = useCallback(async () => {
    setIsSaving(true)
    try {
      const toUpdate = rows
        .filter((r) => r.id > 0)
        .map((r) => ({
          id: r.id,
          colors: r.colors,
          format: r.format,
          price: r.price,
          quantity: r.quantity,
          size: r.size,
          size_range: r.size_range,
          number_of_stitches: r.number_of_stitches,
        }))
      if (toUpdate.length) await onSave(toUpdate)
      if (onAdd) {
        for (const r of rows.filter((x) => x.id < 0)) {
          await onAdd({
            vendor,
            print_code: printCode,
            colors: r.colors,
            format: r.format,
            price: r.price,
            quantity: r.quantity,
            size: r.size,
            size_range: r.size_range,
            number_of_stitches: r.number_of_stitches,
          })
        }
      }
    } finally {
      setIsSaving(false)
    }
  }, [rows, vendor, printCode, onSave, onAdd])

  const inputCls = 'h-9 border-0 bg-transparent focus-visible:ring-1 min-w-0'

  const rawInitialValue = useMemo(
    () =>
      `[\n${data
        .map((r) =>
          JSON.stringify(
            {
              id: r.id,
              colors: r.colors,
              format: r.format,
              price: r.price,
              quantity: r.quantity,
              size: r.size,
              size_range: r.size_range,
              number_of_stitches: r.number_of_stitches,
            },
            null,
            2
          )
        )
        .join(',\n')}\n]`,
    [data]
  )

  const toNum = (v: unknown) =>
    typeof v === 'number' ? v : v != null ? Number(v) : null
  const toStr = (v: unknown) => (v != null ? String(v) : null)

  const handleRawSave = useCallback(
    async (parsed: Record<string, unknown>[]) => {
      const existingIds = new Set(data.map((r) => r.id))
      const parsedIds = new Set(
        parsed.map((r) => Number(r?.id)).filter((id) => id > 0)
      )
      const toUpdate = parsed
        .filter((r) => Number(r?.id) > 0)
        .map((r) => ({
          id: Number(r.id),
          colors: toNum(r.colors),
          format: toStr(r.format),
          price: toNum(r.price),
          quantity: toNum(r.quantity),
          size: toStr(r.size),
          size_range: toStr(r.size_range),
          number_of_stitches: toStr(r.number_of_stitches),
        }))
      const toCreate = parsed
        .filter((r) => !r?.id || Number(r.id) <= 0)
        .map((r) => ({
          vendor,
          print_code: printCode,
          colors: toNum(r.colors),
          format: toStr(r.format),
          price: toNum(r.price),
          quantity: toNum(r.quantity),
          size: toStr(r.size),
          size_range: toStr(r.size_range),
          number_of_stitches: toStr(r.number_of_stitches),
        }))
      const toDelete = [...existingIds].filter((id) => !parsedIds.has(id))
      if (toUpdate.length) await onSave(toUpdate)
      for (const r of toCreate) await onAdd(r)
      for (const id of toDelete) await onDelete(id)
    },
    [data, vendor, printCode, onSave, onAdd, onDelete]
  )

  return (
    <Card>
      <Collapsible defaultOpen>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between px-4 py-2">
            <span className="font-medium">Decoration unit cost</span>
            <span className="text-muted-foreground">▼</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-4 pt-2">
            <Row className="items-center gap-2">
              <span className="text-sm text-muted-foreground">Edit mode:</span>
              <ToggleGroup
                type="single"
                value={entryMode}
                onValueChange={(v) => v && setEntryMode(v as 'table' | 'raw')}
                variant="outline"
              >
                <ToggleGroupItem value="table">
                  <Table2 className="h-4 w-4 mr-2" />
                  Table
                </ToggleGroupItem>
                <ToggleGroupItem value="raw">
                  <Code className="h-4 w-4 mr-2" />
                  Raw
                </ToggleGroupItem>
              </ToggleGroup>
            </Row>

            {entryMode === 'raw' ? (
              <RawTableEditor
                initialValue={rawInitialValue}
                dataKey={data}
                onSave={handleRawSave}
              />
            ) : (
              <>
                <Column className="border rounded-md overflow-auto max-h-[400px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>ID</TableHead>
                        <TableHead>Colors</TableHead>
                        <TableHead>Format</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Size range</TableHead>
                        <TableHead>Stitches</TableHead>
                        <TableHead>Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.length === 0 && !isLoading ? (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            className="text-center text-muted-foreground py-6"
                          >
                            No rows
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows.map((row, i) => (
                          <TableRow key={row.id}>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => remove(i)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {row.id > 0 ? row.id : '–'}
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={row.colors ?? ''}
                                onChange={(e) =>
                                  update(i, 'colors', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={row.format ?? ''}
                                onChange={(e) =>
                                  update(i, 'format', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={row.price ?? ''}
                                onChange={(e) =>
                                  update(i, 'price', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                value={row.quantity ?? ''}
                                onChange={(e) =>
                                  update(i, 'quantity', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={row.size ?? ''}
                                onChange={(e) =>
                                  update(i, 'size', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={row.size_range ?? ''}
                                onChange={(e) =>
                                  update(i, 'size_range', e.target.value)
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={row.number_of_stitches ?? '' }
                                onChange={(e) =>
                                  update(
                                    i,
                                    'number_of_stitches',
                                    e.target.value
                                  )
                                }
                                className={inputCls}
                              />
                            </TableCell>
                            <TableCell className="text-muted-foreground tabular-nums">
                              {formatDate(row.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Column>
                <Row className="gap-2">
                  <Button variant="secondary" size="sm" onClick={add}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add row
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={save}
                    disabled={isSaving}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </Row>
              </>
            )}
          </Column>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
